import React, { useEffect, useState, useRef } from 'react';
import { Plus, Check, ChevronDown } from 'lucide-react';
import { Borrower } from '../data/borrowers';
interface QueueRowProps {
  borrower: Borrower;
  onClick: () => void;
  onStatusChange: (borrowerId: string, newStatus: 'Attempted' | 'Connected' | 'Converted') => void;
  onCreateLoan: (borrowerId: string) => void;
  onMarkComplete: (borrowerId: string) => void;
}
const STATUS_OPTIONS: Array<'New' | 'Attempted' | 'Connected' | 'Converted'> = ['New', 'Attempted', 'Connected', 'Converted'];
const STATUS_STYLES = {
  New: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200',
  Attempted: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
  Connected: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
  Converted: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
};
export function QueueRow({
  borrower,
  onClick,
  onStatusChange,
  onCreateLoan,
  onMarkComplete
}: QueueRowProps) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showStatusMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusMenu]);
  const rowTints = {
    New: '',
    Attempted: 'bg-orange-50/30 border-l-orange-400',
    Connected: 'bg-blue-50/30 border-l-blue-400',
    Converted: 'bg-green-50/30 border-l-green-400'
  };
  const handleStatusSelect = (newStatus: 'Attempted' | 'Connected' | 'Converted') => {
    onStatusChange(borrower.id, newStatus);
    setShowStatusMenu(false);
  };
  return <tr className={`border-b border-slate-200 hover:shadow-md hover:bg-slate-50/50 transition-all cursor-pointer group border-l-4 ${rowTints[borrower.status]}`} onClick={onClick} data-id="element-139">
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-140">
        <div className="flex items-center gap-3" data-id="element-141">
          <div className="w-10 h-10 rounded-full bg-brand-navy-900 text-white flex items-center justify-center font-semibold text-sm flex-shrink-0" data-id="element-142">
            {borrower.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="font-semibold text-brand-navy-900 text-base truncate" data-id="element-143">
            {borrower.name}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-slate-600 font-mono whitespace-nowrap" data-id="element-144">
        {borrower.loanNumber}
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-145">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap" data-id="element-146">
          {new Date(borrower.fundedDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}
        </span>
      </td>
      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap" data-id="element-147">
        {borrower.loanType}
      </td>
      <td className="px-6 py-4 text-sm text-slate-700 tabular-nums whitespace-nowrap" data-id="element-148">
        {borrower.interestRate.toFixed(3)}%
      </td>
      <td className="px-6 py-4 text-base font-semibold text-slate-900 tabular-nums whitespace-nowrap" data-id="element-149">
        ${borrower.loanAmount.toLocaleString()}
      </td>
      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap truncate" data-id="element-150">
        {borrower.loa}
      </td>
      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap truncate" data-id="element-151">
        {borrower.manager}
      </td>
      <td className="px-6 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-152">
        <div className="relative inline-block" ref={menuRef} data-id="element-153">
          <button type="button" onClick={e => {
          e.stopPropagation();
          setShowStatusMenu(v => !v);
        }} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${STATUS_STYLES[borrower.status]}`} title="Click to change status" data-id="element-154">
            {borrower.status}
            <ChevronDown className="w-3 h-3" data-id="element-155" />
          </button>
          {showStatusMenu && <div className="absolute left-0 mt-1 w-36 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1" data-id="element-156">
              {STATUS_OPTIONS.filter(s => s !== 'New').map(status => <button key={status} type="button" onClick={e => {
            e.stopPropagation();
            handleStatusSelect(status as 'Attempted' | 'Connected' | 'Converted');
          }} className={`w-full px-3 py-1.5 text-left text-sm flex items-center justify-between ${borrower.status === status ? 'bg-slate-50 font-medium' : 'hover:bg-slate-50'} text-slate-700`} data-id="element-157">
                  <span data-id="element-158">{status}</span>
                  {borrower.status === status && <Check className="w-3.5 h-3.5 text-brand-orange-500" data-id="element-159" />}
                </button>)}
            </div>}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap" data-id="element-160">
        {new Date(borrower.lastActivity).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })}
      </td>
      <td className="px-6 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-161">
        <div className="flex items-center gap-2" data-id="element-162">
          <button onClick={e => {
          e.stopPropagation();
          onCreateLoan(borrower.id);
        }} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors" data-id="element-163">
            <Plus className="w-4 h-4" data-id="element-164" />
            Create Loan
          </button>

          <button onClick={e => {
          e.stopPropagation();
          onMarkComplete(borrower.id);
        }} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-165">
            <Check className="w-4 h-4" data-id="element-166" />
            Mark Complete
          </button>
        </div>
      </td>
    </tr>;
}